import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { billingApi, type BillingCycle } from '../api/client'
import { useAuthStore } from '../store/authStore'
import { useInstances } from '../hooks/useInstances'
import { track } from '../lib/tracker'
import { formatMoney, formatMinorTRY, catalogPrice } from '../lib/pricing'
import { useProduct } from '../hooks/useCatalog'
import { useDisplayCurrency } from '../store/currencyStore'
import GrantBanner from '../components/GrantBanner'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  LinkButton,
  PageHeader,
  Skeleton,
  type BadgeTone,
} from '../components/ui'

const PLAN_DISPLAY: Record<string, string> = {
  free: 'Free',
  paid: 'Paid',
}

// Subscription status → the badge role it maps onto. Anything unknown is a
// state the customer should look at, so it lands on warning rather than silent.
const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'success',
  canceled: 'danger',
}

export default function BillingPage() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const checkoutSuccess = searchParams.get('checkout') === 'success'
  const currentPlan = useAuthStore((s) => s.plan) ?? 'free'
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'plan' | 'invoices'>('plan')
  const [actionError, setActionError] = useState('')
  const [confirmDeleteCard, setConfirmDeleteCard] = useState(false)
  const currency = useDisplayCurrency()

  const { data: sub, isLoading: subLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: billingApi.getSubscription,
  })

  // No plans query here any more: the only thing this page took from
  // /billing/plans was the price, and prices now come from the catalog
  // (useProduct below), which is the table the charge is priced against.
  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: billingApi.getInvoices,
  })

  const { data: instances } = useInstances()

  useEffect(() => {
    if (!checkoutSuccess) return
    window.scrollTo(0, 0)
    track('subscription_activated')
    ;(async () => {
      try {
        const { data } = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true })
        useAuthStore.getState().setAccessToken(data.access_token, data.role, data.plan, data.language)
      } catch {
        // best-effort; backend RequirePlan now also re-checks DB
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['subscription'] }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['instances'] }),
      ])
    })()
  }, [checkoutSuccess, queryClient])

  const deleteCardMutation = useMutation({
    mutationFn: () => billingApi.deleteCard(),
    onSuccess: () => {
      setConfirmDeleteCard(false)
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
    },
    onError: () => setActionError(t('billing.cardActionError')),
  })

  const updateCardMutation = useMutation({
    mutationFn: () => billingApi.updateCard(),
    onSuccess: (data) => {
      window.location.href = data.payment_page_url
    },
    onError: () => setActionError(t('billing.cardActionError')),
  })

  const plan = sub?.plan ?? currentPlan
  const cycle: BillingCycle = sub?.billing_cycle ?? 'monthly'
  const extraInstances = sub?.extra_instances ?? 0
  const totalInstances = plan === 'paid' ? extraInstances : 1

  // A time-limited admin grant (comped "1 year free") lands as plan=paid with no
  // subscription row: no card, no next_charge_at, no real charge amount. Detect
  // it so we never show a bogus price or renewal to a comped customer.
  const isGrant = !!sub?.plan_expires_at
  const used = instances?.length ?? 0
  const usedPct = Math.min(100, Math.round((used / Math.max(totalInstances, 1)) * 100))

  const mailAddon = !!sub?.mail_addon
  // Amount actually billed this period, shown in the display currency.
  // TRY uses the locked combined charge (already includes mail); USD/EUR are
  // derived from the catalog stickers (instances, plus mail while the add-on
  // is still active on the account), the same table the charge was priced from.
  const slot = useProduct('slot')
  const mail = useProduct('mail')
  const periodAmount = (() => {
    if (plan !== 'paid' || isGrant) return null
    if (currency === 'TRY' && sub?.charge_amount_minor) return formatMinorTRY(sub.charge_amount_minor)
    if (slot) {
      const amt = catalogPrice(slot, cycle, currency) * totalInstances +
        (mailAddon ? catalogPrice(mail, cycle, currency) : 0)
      return formatMoney(amt, currency, i18n.language)
    }
    return null
  })()

  const cycleLabel = cycle === 'yearly' ? t('billing.cycleYearly') : t('billing.cycleMonthly')
  const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString(i18n.language) : '')

  const statusPill = (
    <Badge tone={sub?.status ? STATUS_TONE[sub.status] ?? 'warning' : 'success'}>
      {sub?.status ? t(`billing.status.${sub.status}`) : t('billing.status.active')}
    </Badge>
  )

  return (
    <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl">
      <PageHeader title={t('billing.title')} description={t('billing.subtitle')} />

      {checkoutSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-xl px-4 py-3 mb-6">
          {t('billing.checkoutSuccess')}
        </div>
      )}

      {/* Tabs. Deliberately NOT SegmentedControl: an underline tab bar is a
          different idiom from the chip-on-grey toggle, and swapping it would be
          a redesign rather than an extraction. */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {(['plan', 'invoices'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t(`billing.tab.${k}`)}
          </button>
        ))}
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-6" role="alert">
          {actionError}
        </div>
      )}

      {tab === 'plan' && (
        subLoading ? (
          <Card>
            <Skeleton className="h-6 w-1/4 mb-3" />
            <Skeleton className="h-4 w-1/3" />
          </Card>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Main — plan identity, usage, grant */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="relative overflow-hidden">
              <div className="pointer-events-none absolute -top-24 -right-16 w-64 h-64 rounded-full bg-brand-500/[0.06] blur-3xl" />
              <div className="relative">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-3xl font-bold text-gray-900">{isGrant ? t('billing.planGranted') : (PLAN_DISPLAY[plan] ?? plan)}</span>
                    {plan === 'paid' && !isGrant && <Badge tone="accent">{cycleLabel}</Badge>}
                    {statusPill}
                  </div>
                  <Link to="/pricing" className="text-sm text-brand-600 hover:text-brand-700 font-medium shrink-0">
                    {plan === 'free' ? t('billing.upgrade') : t('billing.addMoreInstances')}
                  </Link>
                </div>

                {isGrant && (
                  <p className="mt-1 text-sm text-brand-700 font-medium">{t('billing.grantFree')}</p>
                )}

                {/* Time-limited grant countdown */}
                {sub?.plan_expires_at && (
                  <div className="mt-4">
                    <GrantBanner expiresAt={sub.plan_expires_at} />
                  </div>
                )}

                {/* Instance usage */}
                <div className="mt-5">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('billing.usageTitle')}</span>
                    <span className="text-sm text-gray-700 tabular-nums">
                      <span className="font-semibold text-gray-900">{used}</span>
                      <span className="text-gray-400"> / {totalInstances}</span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${usedPct >= 100 ? 'bg-amber-500' : 'bg-brand-500'}`}
                      style={{ width: `${Math.max(usedPct, used > 0 ? 6 : 0)}%` }}
                    />
                  </div>
                </div>

                {mailAddon && (
                  <p className="mt-4 text-sm text-gray-500 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l9 6 9-6M3 8v8a2 2 0 002 2h14a2 2 0 002-2V8M3 8l9-5 9 5" />
                    </svg>
                    {t('billing.mailAddonIncluded')}
                  </p>
                )}

                {sub?.canceled_at && (
                  <p className="mt-3 text-sm text-amber-600">
                    {t('billing.canceledOn', { date: fmtDate(sub.canceled_at) })}
                  </p>
                )}
              </div>
            </Card>

            {/* Saved card — real paid subs only (grants have no card) */}
            {plan === 'paid' && !isGrant && (
              <Card>
                <CardHeader title={t('billing.savedCard')} className="mb-4" />
                {sub?.iyzico_card_last_four ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-7 rounded bg-gradient-to-br from-ink-700 to-ink-900 shrink-0" />
                      <p className="text-sm font-medium text-gray-900">
                        •••• {sub.iyzico_card_last_four}
                        {sub.iyzico_card_brand && (
                          <span className="ml-2 text-gray-500">{sub.iyzico_card_brand.replace('_', ' ')}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <LinkButton
                        tone="accent"
                        onClick={() => updateCardMutation.mutate()}
                        disabled={updateCardMutation.isPending}
                      >
                        {updateCardMutation.isPending ? t('pricing.redirecting') : t('billing.updateCard')}
                      </LinkButton>
                      {confirmDeleteCard ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{t('billing.deleteCardConfirm')}</span>
                          <Button
                            variant="danger"
                            size="sm"
                            loading={deleteCardMutation.isPending}
                            onClick={() => deleteCardMutation.mutate()}
                          >
                            {t('billing.deleteCardYes')}
                          </Button>
                          <LinkButton tone="neutral" onClick={() => setConfirmDeleteCard(false)}>
                            {t('billing.deleteCardNo')}
                          </LinkButton>
                        </div>
                      ) : (
                        <LinkButton tone="danger" onClick={() => setConfirmDeleteCard(true)}>
                          {t('billing.deleteCard')}
                        </LinkButton>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{t('billing.updateCardCaveat')}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">{t('billing.noCard')}</p>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar — billing facts (fills the desktop right side) */}
          <div className="space-y-6">
            {plan === 'paid' && !isGrant ? (
              <Card>
                <CardHeader title={t('billing.summaryTitle')} className="mb-4" />
                <dl className="space-y-3 text-sm">
                  {periodAmount && (
                    <div className="flex items-center justify-between">
                      <dt className="text-gray-500">{t('billing.factAmountLabel')}</dt>
                      <dd className="font-semibold text-gray-900">
                        {periodAmount}
                        <span className="text-gray-400 font-normal">/{cycle === 'yearly' ? t('pricing.yr') : t('pricing.mo')}</span>
                      </dd>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">{t('billing.factCycleLabel')}</dt>
                    <dd className="text-gray-700">{cycleLabel}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">{t('billing.factLastChargeLabel')}</dt>
                    <dd className="text-gray-700">{sub?.last_charge_at ? fmtDate(sub.last_charge_at) : t('billing.notChargedYet')}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-gray-500">{t('billing.factNextChargeLabel')}</dt>
                    <dd className={sub?.next_charge_at && sub.status !== 'canceled' ? 'text-gray-700' : 'text-amber-600'}>
                      {sub?.next_charge_at && sub.status !== 'canceled' ? fmtDate(sub.next_charge_at) : t('billing.autoRenewOff')}
                    </dd>
                  </div>
                </dl>
              </Card>
            ) : (
              <Card className="relative overflow-hidden">
                <div className="pointer-events-none absolute -bottom-16 -right-12 w-48 h-48 rounded-full bg-brand-500/[0.07] blur-3xl" />
                <div className="relative">
                  <CardHeader title={t('billing.freePromoTitle')} description={t('billing.freePromoText')} className="mb-4" />
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-1.5 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
                  >
                    {t('billing.viewPlans')}
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </Card>
            )}
          </div>
        </div>
        )
      )}

      {tab === 'invoices' && (
        <Card padded={false} className="overflow-hidden">
          {invoicesLoading ? (
            <div className="p-5 space-y-3">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-1/4" />
            </div>
          ) : !invoices || invoices.length === 0 ? (
            <EmptyState title={t('billing.invoicesEmpty')} />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-400 uppercase tracking-wide">
                  <th className="px-4 py-3 font-medium">{t('billing.invoiceDate')}</th>
                  <th className="px-4 py-3 font-medium">{t('billing.invoiceDescription')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('billing.invoiceAmount')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('billing.invoiceStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {new Date(inv.created_at).toLocaleString(i18n.language)}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {inv.description ?? '-'}
                      {inv.iyzico_payment_id && (
                        <span className="block text-xs text-gray-400">#{inv.iyzico_payment_id}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-900 font-medium text-right whitespace-nowrap">
                      {formatMinorTRY(inv.amount_minor)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Badge tone={inv.status === 'paid' ? 'success' : 'danger'}>
                        {t(`billing.invoiceStatus_${inv.status}`)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
